import * as React from 'react'

import mapboxgl, { MapMouseEvent, MapTouchEvent } from 'mapbox-gl'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { unstable_batchedUpdates } from 'react-dom'
import {
  MapProps,
  MarkerDragEvent,
  ViewState,
  ViewStateChangeEvent,
} from 'react-map-gl/mapbox'

import { IPosition, IProjectAddressInput, LocationType } from '~/client/graph'
import type { LocationIconName } from '~/client/src/shared/components/SitemapHelpers/utils/markerSpec'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'

import Language from '../../localization/Language'
import ProjectAddressMarker from '../ProjectAdressMarker/ProjectAdressMarker'
import {
  getToolCursor,
  isDragAffordanceCursor,
} from '../SitemapHelpers/utils/locationCursor'
import {
  ITEM_EDITOR_SHAPE_FILL_LAYER,
  ITEM_EDITOR_SHAPE_LINE_LAYER,
  POLYLINE_ADDITIONAL_EDITABLE_POINTS_LAYER,
  SHAPE_ARROW_LINE_LAYER,
  SHAPE_EDITABLE_POINTS_LAYER,
  SHAPE_FILL_LAYER,
  SHAPE_LINE_LAYER,
  TILESET_CIRCLE_LAYER_PREFIX,
  TILESET_FILL_LAYER_PREFIX,
  TILESET_LINE_LAYER_PREFIX,
  TILESET_SYMBOL_LAYER_PREFIX,
  UNDER_PLAN_FILL_LAYER,
} from './mapboxConstants'
import { getMapBoxPlaceFromBareCoordinates } from './mapboxGeocoding'
import MapBoxViewerStore, {
  ISitemapWithBasemap,
  mapboxFeatureToAddress,
} from './MapBoxViewer.store'
import MapCanvas, { MapStyleGate } from './MapCanvas'
import { isUserDrivenMove } from './userDrivenMove'

export interface IMapEvents {
  onMouseDown?: (evt: MapMouseEvent) => void
  onMouseUp?: (evt: MapMouseEvent) => void
  onContextMenu?: (evt: MapMouseEvent) => void
  onMouseMove?: (evt: MapMouseEvent) => void
  onMapClick?: (evt: MapMouseEvent) => void
  onMapDblClick?: (evt: MapMouseEvent) => void
  onKeyDown?: (evt: KeyboardEvent) => void
  onKeyUp?: (evt: KeyboardEvent) => void
  onTouchStart?: (evt: MapTouchEvent) => void
  onTouchEnd?: (evt: MapTouchEvent) => void
}

interface IMapBoxEditorProps {
  latitude: number
  longitude: number
  store: MapBoxViewerStore
  language: Language

  mapEvents?: IMapEvents

  creatableAttributeType?: LocationType
  creatableAttributeIcon?: LocationIconName
  isTextStickerCreationActive?: boolean

  createAttributeInPosition?: (position: IPosition) => void
  width?: number
  height?: number

  // Plan alignment; the camera has to stay flat through it.
  isRubberMode?: boolean
  mapOpacity?: number

  containerWidth?: number
  containerHeight?: number
  globeLayer?: (containerWidth: number, containerHeight: number) => JSX.Element
  updateLngLat?: (latitude: number, longitude: number) => void
  onAddressChanged?: (address: IProjectAddressInput) => void
  isLogisticsView?: boolean
  isDeliveryView?: boolean
  shouldShowProjectMarker?: boolean

  isGlobeMode?: boolean
  sitemapWithBasemapsOnGlobe?: ISitemapWithBasemap[]
  globeId?: string
  renderCenterMarker?: () => JSX.Element
  disableMapInteractions?: boolean
  // A host-owned basemap: the globe's saved style is then never applied.
  mapStyleOverride?: MapProps['mapStyle']
  isPitchLocked?: boolean
}

const MAX_SIZE = '100%'

@observer
export class MapBoxViewer extends React.Component<IMapBoxEditorProps> {
  @observable private localProps: IMapBoxEditorProps = null
  // The camera the map opens on. Captured once and never re-read: the map owns its camera from
  // here on, and handing the mirror back every frame is what made terrain panning chase itself
  // (STX1-1029). Hosts reposition through the store's camera methods instead.
  private readonly initialViewState: Partial<ViewState>

  public constructor(props: IMapBoxEditorProps) {
    super(props)
    this.localProps = props
    const { latitude, longitude, zoom, pitch, bearing } =
      props.store.viewport ?? {}
    this.initialViewState = { latitude, longitude, zoom, pitch, bearing }
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IMapBoxEditorProps>): void {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
    if (
      (this.props.containerHeight !== prevProps.containerHeight &&
        !!prevProps.containerHeight) ||
      (this.props.containerWidth !== prevProps.containerWidth &&
        !!prevProps.containerWidth)
    ) {
      this.props.store.onResize()
    }
    const shouldUpdateOrder: boolean =
      this.props.sitemapWithBasemapsOnGlobe !==
        prevProps.sitemapWithBasemapsOnGlobe ||
      this.props.sitemapWithBasemapsOnGlobe?.length !==
        prevProps.sitemapWithBasemapsOnGlobe?.length ||
      (this.props.globeId &&
        this.props.globeId !== prevProps.globeId &&
        !!this.props.store.mapRef)

    if (shouldUpdateOrder) {
      this.props.store.keepShapesAboveOverlays()
    }
    if (this.props.language !== prevProps.language) {
      this.props.store.updateLanguage()
    }
    if (
      this.props.globeId &&
      this.props.globeId !== prevProps.globeId &&
      this.props.mapStyleOverride === undefined
    ) {
      this.props.store.setInitStyle()
    }
  }

  public componentDidMount(): void {
    document.addEventListener('keydown', this.onKeyDown)
    document.addEventListener('keyup', this.onKeyUp)
    if (this.props.mapStyleOverride === undefined) {
      this.props.store.setInitStyle()
    }
  }

  public componentWillUnmount(): void {
    this.unmount()
  }

  @action
  private unmount = () => {
    this.props.store.removeRefs()
    document.removeEventListener('keydown', this.onKeyDown)
    document.removeEventListener('keyup', this.onKeyUp)
  }

  public render(): JSX.Element {
    const {
      store,
      shouldShowProjectMarker,
      width,
      height,
      containerWidth,
      containerHeight,
      isTextStickerCreationActive,
      isGlobeMode,
      isRubberMode,
      disableMapInteractions,
    } = this.props

    // The address-editing pin only; the low-zoom fallback belongs to MapLocationLayers.
    const shouldRenderProjectMarker: boolean = shouldShowProjectMarker

    return (
      <div
        id="map"
        className={classList({
          // mapbox-editor and map-box-editor-map are names ProjectCreation.scss targets, not styles.
          'relative mapbox-editor map-box-editor-map full-width full-height': true,
          'full-width full-height': !width && !height,
          // Fade the WebGL surface only; controls and markers stay opaque.
          'tw:[&_canvas]:opacity-[var(--map-opacity,1)]': true,
          'tw:[&_.mapboxgl-popup]:z-[3]': true,
          // Suppresses the tool cursor, and any inherited one, while a text sticker is placed.
          'tw:cursor-[unset]! tw:[&_div]:cursor-[unset]!':
            isTextStickerCreationActive,
        })}
        style={{
          width: containerWidth ? containerWidth : MAX_SIZE,
          height: containerHeight ? containerHeight : MAX_SIZE,
          zIndex: 1,
          // @ts-expect-error CSS variable for canvas opacity
          '--map-opacity': this.props.mapOpacity ?? 1,
        }}
      >
        <MapCanvas
          projection={store.projection}
          onStyleLoadedChange={store.setStyleLoaded}
          isTrafficEnabled={store.globeViewStyle.isTrafficEnabled}
          isTerrainEnabled={store.globeViewStyle.isTerrainEnabled}
          isThreeDEnabled={store.globeViewStyle.isThreeDEnabled}
          shouldHideLabels={store.globeViewStyle.shouldHideLabels}
          isPitchLocked={isRubberMode || !!this.props.isPitchLocked}
          onMouseDown={this.onMouseDown}
          onMouseUp={this.onMouseUp}
          onMouseMove={this.onMouseMove}
          onContextMenu={this.onContextMenu}
          onDblClick={this.onMapDblClick}
          onClick={this.onMapClick}
          onTouchStart={this.onTouchStart}
          onTouchEnd={this.onTouchEnd}
          ref={store.setRefForProjectMap}
          initialViewState={this.initialViewState}
          onMove={this.onMove}
          mapStyle={this.props.mapStyleOverride ?? store.mapStyle}
          preserveDrawingBuffer={true}
          cursor={this.cursor}
          isInert={disableMapInteractions}
          style={{
            width: width ? containerWidth : MAX_SIZE,
            height: height ? containerHeight : MAX_SIZE,
          }}
          interactiveLayerIds={
            disableMapInteractions ? [] : this.interactiveLayers
          }
        >
          <MapStyleGate>
            <>
              {/* all site maps and items*/}
              {isGlobeMode && this.globe}
              {/* project marker [at some zoom point OR  project creation] */}
              {shouldRenderProjectMarker && this.projectAddressMarker}
              {/* custom center marker overlay (render last to ensure it stays on top) */}
              {this.props.renderCenterMarker && this.centerMarker}
            </>
          </MapStyleGate>
        </MapCanvas>
      </div>
    )
  }

  // Batch: runs every camera frame outside React's event system, so React 17
  // would otherwise commit ~3× per frame while panning.
  // originalEvent is spelled out because the camera event types omit it.
  private onMove = (e: ViewStateChangeEvent & { originalEvent?: Event }) => {
    unstable_batchedUpdates(() => {
      this.props.store.setViewport(e.viewState, isUserDrivenMove(e))
    })
  }

  private get interactiveLayers(): string[] {
    return [
      SHAPE_FILL_LAYER,
      SHAPE_LINE_LAYER,
      ITEM_EDITOR_SHAPE_FILL_LAYER,
      ITEM_EDITOR_SHAPE_LINE_LAYER,
      SHAPE_EDITABLE_POINTS_LAYER,
      POLYLINE_ADDITIONAL_EDITABLE_POINTS_LAYER,
      SHAPE_ARROW_LINE_LAYER,
      UNDER_PLAN_FILL_LAYER,
      ...(this.props.store.tilesetsControlStore?.globeTilesets || []).flatMap(
        t => [
          TILESET_FILL_LAYER_PREFIX + t.id,
          TILESET_LINE_LAYER_PREFIX + t.id,
          TILESET_CIRCLE_LAYER_PREFIX + t.id,
          TILESET_SYMBOL_LAYER_PREFIX + t.id,
        ],
      ),
    ]
  }

  private get cursor(): string | undefined {
    const {
      isTextStickerCreationActive,
      creatableAttributeType,
      creatableAttributeIcon,
      store: {
        mapCursor,
        isOverlayPointerHovered,
        hoveredMarkerItemId,
        markupEditorCursor,
      },
    } = this.props
    // A drag affordance outranks the hover pointer, both ways round: the object being dragged
    // sits under the cursor for the whole gesture, so letting hover answer flickered the cursor
    // between the two until the drag ended — and a SELECTED object is draggable, not a link, so
    // the pointer belongs to unselected objects only.
    if (isDragAffordanceCursor(mapCursor)) {
      return mapCursor
    }
    // Overlay layers aren't in interactiveLayerIds — their hover arrives via the
    // store. This prop is the canvas cursor's one writer: react-map-gl writes it only
    // when it changes, so a second writer would strip it until the next change.
    if (isOverlayPointerHovered || hoveredMarkerItemId) {
      return 'pointer'
    }
    if (markupEditorCursor) {
      return markupEditorCursor
    }
    // No 'default' fallback: the inert shield already sets it on the canvas, the same element
    // react-map-gl writes this prop to. Only an explicit mapCursor still has anything to say.
    if (this.props.disableMapInteractions) {
      return mapCursor
    }
    const toolCursor = getToolCursor(
      creatableAttributeType,
      isTextStickerCreationActive
        ? MapViewLocationIcon.TextBox
        : creatableAttributeIcon,
    )
    if (toolCursor) {
      return toolCursor
    }
    if (mapCursor) {
      return mapCursor
    }
    // Undefined, not 'grab': an inline cursor outranks mapbox's own grab/grabbing classes.
    return undefined
  }

  private onTouchStart = (e: MapTouchEvent) => {
    if (this.props.disableMapInteractions) return
    this.props.mapEvents?.onTouchStart?.(e)
  }

  private onTouchEnd = (e: MapTouchEvent) => {
    if (this.props.disableMapInteractions) return
    this.props.mapEvents?.onTouchEnd?.(e)
  }

  private onMapClick = (e: MapMouseEvent) => {
    if (this.props.disableMapInteractions) return
    if (
      this.props.creatableAttributeType ||
      this.props.isTextStickerCreationActive
    ) {
      this.props.createAttributeInPosition(
        this.props.store.mapRef.getMap().project(e.lngLat),
      )
      return
    }

    const hadSelectedMarker = !!this.props.store.selectedMarkerItemId
    if (hadSelectedMarker) {
      runInAction(() => {
        this.props.store.selectedMarkerItemId = null
      })
    }

    this.props.mapEvents?.onMapClick?.(e)
  }

  private onMapDblClick = (e: MapMouseEvent) => {
    if (this.props.disableMapInteractions) return
    this.props.mapEvents?.onMapDblClick?.(e)
    if (this.props.shouldShowProjectMarker) {
      const { lat, lng } = e.lngLat
      this.changeAddress(lat, lng)
    }
  }

  private onMouseDown = (evt: MapMouseEvent) => {
    if (this.props.disableMapInteractions) return
    this.props.mapEvents?.onMouseDown?.(evt)
  }

  // Mapbox suppresses the native menu itself and only fires this for a right CLICK: an active
  // right-drag blocks the handler that would have flushed it, so rotate/pitch and this event are
  // mutually exclusive with no tolerance check of ours (STX1-1061).
  private onContextMenu = (evt: MapMouseEvent) => {
    if (this.props.disableMapInteractions) return
    this.props.mapEvents?.onContextMenu?.(evt)
  }

  private onMouseUp = (evt: MapMouseEvent) => {
    if (this.props.disableMapInteractions) return
    this.props.mapEvents?.onMouseUp?.(evt)
  }

  private onMouseMove = (evt: MapMouseEvent) => {
    if (this.props.disableMapInteractions) return
    this.props.mapEvents?.onMouseMove?.(evt)
  }

  private onKeyDown = (e: KeyboardEvent) => {
    this.props.mapEvents?.onKeyDown?.(e)
  }

  private onKeyUp = (e: KeyboardEvent) => {
    this.props.mapEvents?.onKeyUp?.(e)
  }

  private get projectAddressMarker(): JSX.Element {
    const { latitude, longitude, shouldShowProjectMarker } = this.props

    if (!latitude || !longitude) {
      return null
    }

    return (
      <ProjectAddressMarker
        longitude={longitude}
        latitude={latitude}
        draggable={shouldShowProjectMarker}
        onProjectMarkerDrag={this.onProjectMarkerDrag}
        onProjectMarkerDragEnd={this.onProjectMarkerDragEnd}
      />
    )
  }
  private get centerMarker(): JSX.Element {
    const { renderCenterMarker } = this.props
    if (!renderCenterMarker) {
      return null
    }
    const content = renderCenterMarker()
    if (!content) {
      return null
    }
    return (
      <div className="center-pin-marker tw:absolute tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:z-10 tw:pointer-events-none">
        {content}
      </div>
    )
  }

  private get globe(): JSX.Element {
    const { globeLayer, containerWidth, containerHeight } = this.props
    return globeLayer(containerWidth, containerHeight)
  }

  private onProjectMarkerDrag = async (
    event: MarkerDragEvent,
  ): Promise<void> => {
    this.props.updateLngLat(event.lngLat.lat, event.lngLat.lng)
  }

  private onProjectMarkerDragEnd = async (
    event: MarkerDragEvent,
  ): Promise<void> => {
    const { lat, lng } = event.lngLat
    this.props.updateLngLat(lat, lng)
    this.changeAddress(lat, lng)
  }

  @action.bound
  private async changeAddress(lat: number, lng: number) {
    const item = await getMapBoxPlaceFromBareCoordinates(
      lng,
      lat,
      mapboxgl.accessToken,
    )

    const addr = mapboxFeatureToAddress(item)

    this.props.onAddressChanged?.(addr)
  }
}
